/// <reference types="gtag.js" />

import { useCallback, useMemo, useRef, useState } from 'react';

import { loadScript } from '#@/utils/loadScript';

import { areAnalyticsEnabledInEnv } from '../areAnalyticsEnabledInEnv';
import useQueueUntilReady from '../utils/useQueueUntilReady';
import type { Integration, PageViewProps } from './types';

declare global {
  interface Window {
    dataLayer?: Array<unknown>;
    gtag?: Gtag.Gtag;
  }
}

const useGoogleAnalyticsIntegration = (): Integration => {
  const [isInitialized, setIsInitialized] = useState(false);
  const initializationState = useRef<'idle' | 'loading' | 'done'>('idle');
  const enabled = isGoogleTagEnabled();

  const trackEvent = useCallback((eventName: string, eventProps: Record<string, unknown>) => {
    if (!isGoogleTagEnabled()) return;

    window?.gtag?.('event', eventName, eventProps);
  }, []);
  // this is needed since we don't properly integrate gtm for its own queuing functionality (see todo below)
  const initAwareTrackEvent = useQueueUntilReady(trackEvent, isInitialized);

  const trackPageView = useCallback((props: PageViewProps) => {
    if (!isGoogleTagEnabled()) return;

    window?.gtag('event', 'page_view', props);
  }, []);
  // this is needed since we don't properly integrate gtm for its own queuing functionality (see todo below)
  const initAwareTrackPageView = useQueueUntilReady(trackPageView, isInitialized);

  const initialize = useCallback(() => {
    if (!isGoogleTagEnabled() || !process.env.GOOGLE_TAG_ID) return;

    if (initializationState.current !== 'idle') return;
    initializationState.current = 'loading';

    const tagId = process.env.GOOGLE_TAG_ID;

    return new Promise<void>((resolve) => {
      loadScript(`https://www.googletagmanager.com/gtag/js?id=${tagId}`, 'gtag.js', () => {
        // @todo this code should actually get defined initially and not onload of the gtag js script
        const dataLayer = window.dataLayer || [];
        window.dataLayer = dataLayer;
        window.gtag = function gtag() {
          // The value pushed to `dataLayer` must be an `arguments` object.
          // eslint-disable-next-line prefer-rest-params
          dataLayer.push(arguments);
        };
        gtag('js', new Date());
        gtag('config', tagId);
        gtag('consent', 'default', {
          ad_storage: 'granted',
          analytics_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        });
        initializationState.current = 'done';
        setIsInitialized(true);
        resolve();
      });
    });
  }, []);

  return useMemo(
    () => ({
      name: 'gtm',
      initialize,
      enabled,
      trackEvent: initAwareTrackEvent,
      trackPageView: initAwareTrackPageView,
    }),
    [initAwareTrackEvent, initAwareTrackPageView, enabled, initialize],
  );
};

const isGoogleTagEnabled = () =>
  areAnalyticsEnabledInEnv() &&
  Boolean(process.env.GOOGLE_TAG_ID) &&
  process.env.REACT_APP_CYPRESS !== 'true';

export default useGoogleAnalyticsIntegration;
